/**
 * Special relativity: the Lorentz factor, time dilation, length contraction and a
 * constant-speed round trip.
 *
 *   β = v / c                           speed as a fraction of the speed of light
 *   γ = 1 / √(1 − β²)                   the Lorentz factor, 1 ≤ γ < ∞
 *   Δt = γ Δt₀                          moving clocks run slow: Δt₀ is the proper time
 *   L = L₀ / γ                          moving lengths shrink along the motion
 *
 * `1 − β²` is formed as (1 − β)(1 + β), and `γ − 1` as γ²β²/(γ + 1), so a satellite
 * crawling at 4 km/s still gets a meaningful γ − 1 ≈ 8 × 10⁻¹¹ instead of rounding to 0.
 * SI units; c is exact.
 */

import { SPEED_OF_LIGHT } from "@/lib/tool-kit/physics-fields/constants";

export type RelativityError =
  | "missing"
  | "non-positive"
  | "negative-speed"
  | "light-speed"
  | "gamma-below-one"
  | "not-finite";

interface Failure {
  ok: false;
  error: RelativityError;
}

/** Lorentz factor for 0 ≤ β < 1. */
export function lorentzFactor(beta: number): number {
  return 1 / Math.sqrt((1 - beta) * (1 + beta));
}

/** γ − 1 without cancellation: γ²β² / (γ + 1). */
export function gammaMinusOne(beta: number): number {
  const gamma = lorentzFactor(beta);
  return (gamma * gamma * beta * beta) / (gamma + 1);
}

/** The speed β that gives a Lorentz factor γ ≥ 1. */
export function betaFromGamma(gamma: number): number {
  return Math.sqrt((gamma - 1) * (gamma + 1)) / gamma;
}

/* ── Time dilation and length contraction ─────────────────────────────────── */

export interface DilationSolution {
  /** Speed, m/s. */
  speed: number;
  beta: number;
  gamma: number;
  /** γ − 1. */
  excess: number;
  properTime: number;
  /** Time on the clock seen as moving, Δt = γ Δt₀ — in the same unit as the input, s. */
  dilatedTime: number;
  /** Δt − Δt₀: how far the clocks drift apart, s. */
  drift: number;
  properLength: number;
  contractedLength: number;
  /** L₀ − L, m. */
  shortening: number;
}

export function solveDilation(
  speed: number | null,
  properTime: number | null,
  properLength: number | null
): { ok: true; value: DilationSolution } | Failure {
  if (speed === null || properTime === null || properLength === null) {
    return { ok: false, error: "missing" };
  }
  if (speed < 0) {
    return { ok: false, error: "negative-speed" };
  }
  if (!(properTime >= 0 && properLength >= 0)) {
    return { ok: false, error: "non-positive" };
  }
  const beta = speed / SPEED_OF_LIGHT;
  if (beta >= 1) {
    return { ok: false, error: "light-speed" };
  }
  const gamma = lorentzFactor(beta);
  const excess = gammaMinusOne(beta);
  const solution: DilationSolution = {
    speed,
    beta,
    gamma,
    excess,
    properTime,
    dilatedTime: gamma * properTime,
    drift: excess * properTime,
    properLength,
    contractedLength: properLength / gamma,
    shortening: properLength * (excess / gamma),
  };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── Speed from a known ratio ─────────────────────────────────────────────── */

export type RatioKnown = "gamma" | "time" | "length";

export interface SpeedSolution {
  gamma: number;
  beta: number;
  /** Speed, m/s. */
  speed: number;
  excess: number;
}

/**
 * The speed from γ directly, from a dilated and a proper time (γ = Δt/Δt₀), or from a
 * proper and a contracted length (γ = L₀/L).
 */
export function solveSpeed(
  known: RatioKnown,
  a: number | null,
  b: number | null
): { ok: true; value: SpeedSolution } | Failure {
  if (a === null || (known !== "gamma" && b === null)) {
    return { ok: false, error: "missing" };
  }
  if (!(a > 0) || (b !== null && !(b > 0))) {
    return { ok: false, error: "non-positive" };
  }
  let gamma = a;
  if (known === "time") {
    gamma = a / (b as number);
  } else if (known === "length") {
    gamma = a / (b as number);
  }
  if (gamma < 1) {
    return { ok: false, error: "gamma-below-one" };
  }
  const beta = betaFromGamma(gamma);
  const solution = {
    gamma,
    beta,
    speed: beta * SPEED_OF_LIGHT,
    excess: gamma - 1,
  };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── A round trip ─────────────────────────────────────────────────────────── */

export interface TripSolution {
  beta: number;
  gamma: number;
  distance: number;
  /** One-way distance in the traveller's frame, d/γ, m. */
  contractedDistance: number;
  /** Round-trip time by Earth's clocks, 2d/v, s. */
  earthTime: number;
  /** Round-trip time by the traveller's clock, 2d/(γv), s. */
  travellerTime: number;
  /** How much younger the traveller is on return, s. */
  ageDifference: number;
}

/**
 * Out and back at a constant speed, with instantaneous turnarounds — the usual twin
 * paradox idealisation. `distance` is the one-way distance in the Earth frame.
 */
export function solveTrip(
  distance: number | null,
  speed: number | null
): { ok: true; value: TripSolution } | Failure {
  if (distance === null || speed === null) {
    return { ok: false, error: "missing" };
  }
  if (!(distance > 0 && speed > 0)) {
    return { ok: false, error: speed < 0 ? "negative-speed" : "non-positive" };
  }
  const beta = speed / SPEED_OF_LIGHT;
  if (beta >= 1) {
    return { ok: false, error: "light-speed" };
  }
  const gamma = lorentzFactor(beta);
  const earthTime = (2 * distance) / speed;
  const solution: TripSolution = {
    beta,
    gamma,
    distance,
    contractedDistance: distance / gamma,
    earthTime,
    travellerTime: earthTime / gamma,
    ageDifference: earthTime * (1 - 1 / gamma),
  };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}
